import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Paper, Alert, AlertTitle, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { Error as ErrorIcon, ExpandMore, Refresh } from '@mui/icons-material';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  browserInfo: {
    userAgent: string;
    platform: string;
    vendor: string;
    language: string;
    cookieEnabled: boolean;
    onLine: boolean;
  };
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      browserInfo: this.getBrowserInfo(),
    };
  }

  getBrowserInfo() {
    if (typeof window === 'undefined') {
      return {
        userAgent: 'Unknown',
        platform: 'Unknown',
        vendor: 'Unknown',
        language: 'Unknown',
        cookieEnabled: false,
        onLine: false,
      };
    }

    return {
      userAgent: navigator.userAgent || 'Unknown',
      platform: navigator.platform || 'Unknown',
      vendor: navigator.vendor || 'Unknown',
      language: navigator.language || 'Unknown',
      cookieEnabled: navigator.cookieEnabled || false,
      onLine: navigator.onLine || false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details for debugging
    console.error('=== ErrorBoundary Caught Error ===');
    console.error('Error:', error);
    console.error('Error Name:', error.name);
    console.error('Error Message:', error.message);
    console.error('Error Stack:', error.stack);
    console.error('Component Stack:', errorInfo.componentStack);
    console.error('Browser Info:', this.state.browserInfo);
    
    // Check for common compatibility issues
    this.checkCompatibilityIssues(error);

    this.setState({
      error,
      errorInfo,
    });

    // You could also log to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
  }

  checkCompatibilityIssues(error: Error) {
    const issues: string[] = [];

    // Check for React 18 createRoot issue
    if (error.message.includes('createRoot') || error.message.includes('ReactDOM.createRoot')) {
      issues.push('React 18 createRoot API not supported. Consider using React 17 or adding polyfills.');
    }

    // Check for ES6+ features
    if (error.message.includes('Unexpected token') || error.message.includes('SyntaxError')) {
      issues.push('JavaScript syntax not supported. May need Babel transpilation for older browsers.');
    }

    // Check for missing features
    if (error.message.includes('is not defined') || error.message.includes('Cannot read property')) {
      issues.push('Missing JavaScript feature or API. May need polyfills.');
    }

    // Check browser version
    const ua = this.state.browserInfo.userAgent;
    if (ua.includes('iPad') || ua.includes('iPhone')) {
      const versionMatch = ua.match(/OS (\d+)_(\d+)/);
      if (versionMatch) {
        const majorVersion = parseInt(versionMatch[1], 10);
        if (majorVersion < 12) {
          issues.push(`iOS ${majorVersion} detected. This version may not support all required features.`);
        }
      }
    }

    if (ua.includes('Safari') && !ua.includes('Chrome')) {
      const versionMatch = ua.match(/Version\/(\d+)/);
      if (versionMatch) {
        const version = parseInt(versionMatch[1], 10);
        if (version < 12) {
          issues.push(`Safari ${version} detected. This version may not support all required features.`);
        }
      }
    }

    if (issues.length > 0) {
      console.warn('=== Compatibility Issues Detected ===');
      issues.forEach(issue => console.warn('-', issue));
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    // Reload the page to reset the app state
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error, errorInfo, browserInfo } = this.state;

      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 2,
            backgroundColor: '#f5f5f5',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              maxWidth: 800,
              width: '100%',
              padding: 3,
            }}
          >
            <Alert severity="error" sx={{ mb: 3 }}>
              <AlertTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ErrorIcon />
                  <Typography variant="h6">Application Error</Typography>
                </Box>
              </AlertTitle>
              <Typography variant="body1" sx={{ mt: 1 }}>
                Something went wrong while loading the application. This may be due to browser compatibility issues.
              </Typography>
            </Alert>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Error Details
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  backgroundColor: '#fafafa',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  overflow: 'auto',
                  maxHeight: 200,
                }}
              >
                <Typography component="div">
                  <strong>Error:</strong> {error?.name || 'Unknown Error'}
                  <br />
                  <strong>Message:</strong> {error?.message || 'No error message available'}
                </Typography>
              </Paper>
            </Box>

            <Accordion sx={{ mb: 3 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle2">Browser Information</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                  <Typography><strong>User Agent:</strong> {browserInfo.userAgent}</Typography>
                  <Typography><strong>Platform:</strong> {browserInfo.platform}</Typography>
                  <Typography><strong>Vendor:</strong> {browserInfo.vendor}</Typography>
                  <Typography><strong>Language:</strong> {browserInfo.language}</Typography>
                  <Typography><strong>Cookies Enabled:</strong> {browserInfo.cookieEnabled ? 'Yes' : 'No'}</Typography>
                  <Typography><strong>Online:</strong> {browserInfo.onLine ? 'Yes' : 'No'}</Typography>
                </Box>
              </AccordionDetails>
            </Accordion>

            {errorInfo && (
              <Accordion sx={{ mb: 3 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2">Component Stack Trace</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      backgroundColor: '#fafafa',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      overflow: 'auto',
                      maxHeight: 300,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {errorInfo.componentStack}
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}

            {error?.stack && (
              <Accordion sx={{ mb: 3 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2">Error Stack Trace</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      backgroundColor: '#fafafa',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      overflow: 'auto',
                      maxHeight: 300,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {error.stack}
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => {
                  // Copy error details to clipboard
                  const errorText = `
Error: ${error?.name || 'Unknown'}
Message: ${error?.message || 'No message'}
Stack: ${error?.stack || 'No stack'}
Component Stack: ${errorInfo?.componentStack || 'No component stack'}
Browser: ${browserInfo.userAgent}
Platform: ${browserInfo.platform}
                  `.trim();
                  navigator.clipboard?.writeText(errorText).then(() => {
                    alert('Error details copied to clipboard');
                  }).catch(() => {
                    console.log('Error details:', errorText);
                  });
                }}
              >
                Copy Error Details
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Refresh />}
                onClick={this.handleReset}
              >
                Reload Application
              </Button>
            </Box>

            <Box sx={{ mt: 3, p: 2, backgroundColor: '#fff3cd', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Tip:</strong> If this error persists, try:
                <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                  <li>Updating your browser to the latest version</li>
                  <li>Clearing your browser cache and cookies</li>
                  <li>Using a different browser (Chrome, Firefox, or Edge)</li>
                  <li>Checking your internet connection</li>
                </ul>
              </Typography>
            </Box>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

